import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, getApiUrl } from '../../config/api';
import './SalesCostingOverview.css';
import './BomVarianceReport.css';

// BOM against real per production order (#322): what the BOM asked for each material against what was
// issued to the order net of returns, in quantity and cost. COSTING / ADMIN / MANAGER (GET /api/costing/production-variance).
const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
const qty = (v) => parseFloat(v || 0).toFixed(3);
const signed = (v, fmt) => (parseFloat(v || 0) > 0 ? `+${fmt(v)}` : fmt(v));

export const VarianceDetail = ({ productionOrderId }) => {
  const { t } = useTranslation('production');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const res = await apiFetch(getApiUrl(`/api/costing/production-variance/${productionOrderId}`));
        if (!res.ok) throw new Error(String(res.status));
        const body = await res.json();
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setError(t('bomVariance.fetchFailed'));
      }
    })();
    return () => { cancelled = true; };
  }, [productionOrderId, t]);

  if (error) return <div className="error-message">{error}</div>;
  if (!data) return <div className="loading">{t('bomVariance.loading')}</div>;

  return (
    <div className="bom-variance-detail">
      <p className="bom-variance-source">
        {t(`bomVariance.source.${data.cost_source}`)}
      </p>
      <table className="costing-table">
        <thead>
          <tr>
            <th>{t('bomVariance.columns.material')}</th>
            <th>{t('bomVariance.columns.bomQty')}</th>
            <th>{t('bomVariance.columns.realQty')}</th>
            <th>{t('bomVariance.columns.qtyVariance')}</th>
            <th>{t('bomVariance.columns.bomCost')}</th>
            <th>{t('bomVariance.columns.realCost')}</th>
            <th>{t('bomVariance.columns.costVariance')}</th>
          </tr>
        </thead>
        <tbody>
          {data.materials.map(m => (
            <tr key={m.inventory_item_id}>
              <td>
                <strong>{m.item_code}</strong> {m.item_name}
                {!m.in_bom && <span className="bom-variance-tag">{t('bomVariance.notInBom')}</span>}
                {m.alternatives?.length > 0 && (
                  <div className="bom-variance-note">{t('bomVariance.alternatives', { items: m.alternatives.join(', ') })}</div>
                )}
              </td>
              <td>{qty(m.bom_quantity)} {m.unit_of_measure}</td>
              <td title={t('bomVariance.realQtyHint', { issued: qty(m.issued_quantity), returned: qty(m.returned_quantity) })}>
                {qty(m.real_quantity)} {m.unit_of_measure}
              </td>
              <td className={parseFloat(m.quantity_variance) > 0 ? 'negative' : ''}>{signed(m.quantity_variance, qty)}</td>
              <td>{money(m.bom_cost)}</td>
              <td>{money(m.real_cost)}</td>
              <td className={parseFloat(m.cost_variance) > 0 ? 'negative' : ''}>{signed(m.cost_variance, money)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="total-row">
            <td colSpan={4}><strong>{t('bomVariance.total')}</strong></td>
            <td><strong>{money(data.totals.bom_cost)}</strong></td>
            <td><strong>{money(data.totals.real_cost)}</strong></td>
            <td><strong>{signed(data.totals.cost_variance, money)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const BomVarianceReport = () => {
  const { t } = useTranslation('production');
  const [orders, setOrders] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(getApiUrl('/api/costing/production-variance'));
        if (!res.ok) throw new Error(String(res.status));
        setOrders((await res.json()).orders || []);
      } catch (e) {
        setError(t('bomVariance.fetchFailed'));
      }
    })();
  }, [t]);

  if (error) return <div className="sales-costing-overview error">{error}</div>;
  if (!orders) return <div className="sales-costing-overview loading">{t('bomVariance.loading')}</div>;

  return (
    <div className="sales-costing-overview">
      <div className="costing-section">
        <h3>{t('bomVariance.title')}</h3>
        <p className="bom-variance-note">{t('bomVariance.help')}</p>
        {orders.length === 0 ? (
          <div className="no-data">{t('bomVariance.noData')}</div>
        ) : (
          <table className="costing-table">
            <thead>
              <tr>
                <th>{t('bomVariance.columns.order')}</th>
                <th>{t('bomVariance.columns.status')}</th>
                <th>{t('bomVariance.columns.bomCost')}</th>
                <th>{t('bomVariance.columns.realCost')}</th>
                <th>{t('bomVariance.columns.costVariance')}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.production_order_id}
                  className={selected === o.production_order_id ? 'selected' : ''}
                  onClick={() => setSelected(selected === o.production_order_id ? null : o.production_order_id)}
                  style={{ cursor: 'pointer' }}>
                  <td>{o.po_number}</td>
                  <td>{o.status}</td>
                  <td>{money(o.totals.bom_cost)}</td>
                  <td>{o.cost_source === 'REAL' ? money(o.totals.real_cost) : t('bomVariance.noIssues')}</td>
                  <td className={parseFloat(o.totals.cost_variance) > 0 ? 'negative' : ''}>
                    {o.cost_source === 'REAL' ? signed(o.totals.cost_variance, money) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selected && (
        <div className="costing-section">
          <h3>{orders.find(o => o.production_order_id === selected)?.po_number}</h3>
          <VarianceDetail productionOrderId={selected} />
        </div>
      )}
    </div>
  );
};

export default BomVarianceReport;
